import { useCallback, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';

// --- Types ---

interface ExtractedResult {
  summary: string;
  entries: unknown[];
  _signature: string;
}

interface FileUploaderProps {
  onExtracted: (result: ExtractedResult) => void;
}

type UploaderState =
  | 'idle'
  | 'selected'
  | 'uploading'
  | 'extracting'
  | 'error';

interface ErrorInfo {
  message: string;
  /** Whether the user can retry with the same file (5xx), or must pick a new one (4xx/422) */
  allowRetry: boolean;
  /** Whether the user must dismiss the error banner before retry is enabled */
  requireAcknowledge: boolean;
}

// --- Constants ---

const ALLOWED_EXTENSIONS = new Set(['.pdf', '.docx', '.txt', '.md', '.rtf']);
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

function getExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// --- Component ---

export default function FileUploader({ onExtracted }: FileUploaderProps) {
  const [state, setState] = useState<UploaderState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // --- Client-side validation ---

  const validateFile = useCallback((file: File): string | null => {
    const ext = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return 'File type not supported. Allowed: PDF, DOCX, TXT, MD, RTF';
    }
    if (file.size === 0) {
      return 'File is empty (0 bytes)';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File exceeds maximum size (5 MB). Your file is ${formatBytes(file.size)}`;
    }
    return null;
  }, []);

  // --- File selection ---

  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    setAcknowledged(false);
    setValidationError(null);

    const validationMsg = validateFile(file);
    if (validationMsg) {
      setValidationError(validationMsg);
      setSelectedFile(null);
      setState('idle');
      return;
    }

    setSelectedFile(file);
    setState('selected');
  }, [validateFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }, [handleFileSelect]);

  // --- Drag and drop ---

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  // --- Upload and extraction flow ---

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setState('uploading');
    setUploadProgress(0);
    setError(null);

    try {
      // Step 1: Get presigned URL
      const ext = getExtension(selectedFile.name);
      const mimeMap: Record<string, string> = {
        '.pdf': 'application/pdf',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.txt': 'text/plain',
        '.md': 'text/markdown',
        '.rtf': 'application/rtf',
      };

      const { uploadUrl, s3Key } = await api.post<{ uploadUrl: string; s3Key: string; expiresIn: number }>(
        '/api/kb/ingest/upload',
        {
          filename: selectedFile.name,
          contentType: mimeMap[ext] || selectedFile.type,
          fileSize: selectedFile.size,
        }
      );

      // Step 2: Upload to S3 via XMLHttpRequest (for progress tracking)
      await uploadToS3(uploadUrl, selectedFile, mimeMap[ext] || selectedFile.type);

      // Step 3: Call extract endpoint
      setState('extracting');
      const result = await api.post<ExtractedResult>('/api/kb/ingest/extract', {
        source_type: 'file_upload',
        s3_key: s3Key,
      });

      // Step 4: Success — pipe to confirmation UI
      setState('idle');
      setSelectedFile(null);
      onExtracted(result);

    } catch (err) {
      handleError(err);
    }
  }, [selectedFile, onExtracted]);

  // --- S3 upload with progress ---

  const uploadToS3 = useCallback((url: string, file: File, contentType: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new S3UploadError(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new NetworkError('Network error during upload'));
      };

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  }, []);

  // --- Error handling ---

  const handleError = useCallback((err: unknown) => {
    setState('error');

    if (err instanceof NetworkError) {
      // Network error during S3 upload: require acknowledgement before retry
      setError({
        message: 'A network error occurred during upload. Please check your connection and try again.',
        allowRetry: true,
        requireAcknowledge: true,
      });
      setAcknowledged(false);
      return;
    }

    if (err instanceof S3UploadError) {
      // S3 upload failed (non-network)
      setError({
        message: 'Upload failed. Please try again.',
        allowRetry: true,
        requireAcknowledge: true,
      });
      setAcknowledged(false);
      return;
    }

    // API errors — use status code for categorisation
    if (err instanceof ApiError) {
      const { status, message } = err;

      if (status >= 500) {
        // 5xx: generic message, allow retry with same file
        setError({
          message: 'Something went wrong on our end. Please try again.',
          allowRetry: true,
          requireAcknowledge: false,
        });
      } else if (status === 422) {
        // 422: extraction error, allow different file (no auto-retry)
        setError({
          message,
          allowRetry: false,
          requireAcknowledge: false,
        });
      } else {
        // Other 4xx: validation error, show specific message, allow different file
        setError({
          message,
          allowRetry: false,
          requireAcknowledge: false,
        });
      }
      return;
    }

    // Fallback for unexpected errors
    setError({
      message: 'An unexpected error occurred. Please try again.',
      allowRetry: true,
      requireAcknowledge: false,
    });
  }, []);

  // --- Reset / retry ---

  const handleDismissError = useCallback(() => {
    setAcknowledged(true);
  }, []);

  const handleRetry = useCallback(() => {
    setError(null);
    setAcknowledged(false);
    setState('selected');
    // Keep selectedFile — retry with same file
  }, []);

  const handleChooseNewFile = useCallback(() => {
    setError(null);
    setAcknowledged(false);
    setSelectedFile(null);
    setValidationError(null);
    setState('idle');
  }, []);

  // --- Render ---

  const isDisabled = state === 'uploading' || state === 'extracting';

  return (
    <div className="mt-4">
      <h3 className="font-medium text-gray-900 mb-2">Or upload a file</h3>

      {/* Validation error */}
      {validationError && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-sm text-red-700">{validationError}</p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert" aria-live="assertive">
          <p className="text-sm text-red-700 font-medium">{error.message}</p>
          <div className="mt-2 flex gap-2">
            {error.requireAcknowledge && !acknowledged && (
              <button
                onClick={handleDismissError}
                className="text-sm text-red-700 underline hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
              >
                Dismiss
              </button>
            )}
            {error.allowRetry && (!error.requireAcknowledge || acknowledged) && (
              <button
                onClick={handleRetry}
                className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Retry
              </button>
            )}
            {!error.allowRetry && (
              <button
                onClick={handleChooseNewFile}
                className="text-sm bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Choose a different file
              </button>
            )}
          </div>
        </div>
      )}

      {/* Drop zone */}
      {(state === 'idle' || state === 'selected' || state === 'error') && !isDisabled && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Drop a file here or click to browse. Allowed: PDF, DOCX, TXT, MD, RTF (max 5 MB)"
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            focus:outline-none focus:ring-2 focus:ring-blue-500
            ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}
            ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onClick={() => !isDisabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.rtf"
            onChange={handleInputChange}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
            disabled={isDisabled}
          />
          <div className="text-gray-500">
            <p className="text-sm font-medium">
              {dragOver ? 'Drop your file here' : 'Drag & drop a file here, or click to browse'}
            </p>
            <p className="text-xs mt-1 text-gray-400">
              PDF, DOCX, TXT, MD, RTF — max 5 MB
            </p>
          </div>
        </div>
      )}

      {/* Selected file info */}
      {selectedFile && state === 'selected' && (
        <div className="mt-3 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-500 text-lg" aria-hidden="true">📄</span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">{formatBytes(selectedFile.size)}</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handleChooseNewFile}
              className="text-sm text-gray-600 px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Change
            </button>
            <button
              onClick={handleUpload}
              className="text-sm bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 font-medium"
            >
              Upload
            </button>
          </div>
        </div>
      )}

      {/* Upload progress bar */}
      {state === 'uploading' && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-blue-800 font-medium">Uploading…</p>
            <span className="text-sm text-blue-600">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Upload progress">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-blue-500 mt-1">{selectedFile?.name}</p>
        </div>
      )}

      {/* Extraction spinner */}
      {state === 'extracting' && (
        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200 flex items-center gap-3">
          <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full" aria-hidden="true" />
          <div>
            <p className="text-sm text-purple-800 font-medium">Processing file…</p>
            <p className="text-xs text-purple-500">Extracting text and categorising content</p>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Custom error classes for S3 upload error categorisation ---

class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

class S3UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'S3UploadError';
  }
}
